import { useEffect, useRef } from 'react';
import { API_BASE } from './api';
import { getAuth, useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';

/**
 * Real-time push, as one shared connection.
 *
 * ── The backend contract, which shapes everything below ───────────────────
 * `/api/stream` emits exactly ONE event name: `changed`. The specific thing
 * that happened lives in the JSON payload as `reason`:
 *
 *     event: changed
 *     data: {"reason": "dwell_exceeded", "driver_name": "...", ...}
 *
 * There is no `event: order_updated` frame to call addEventListener for.
 * So this module does the demultiplexing: callers subscribe to reasons
 * (`order_updated`, `driver_moved`, `dwell_exceeded`, …) and never touch
 * EventSource. If the backend ever splits into named frames, that is a
 * change here and nowhere else.
 *
 * ── Why a singleton ──────────────────────────────────────────────────────
 * Browsers cap concurrent connections per origin (6 on HTTP/1.1), and an SSE
 * connection is held open indefinitely. One per component would exhaust that
 * budget and starve ordinary requests. One connection, many subscribers.
 *
 * ── One more frame name: `stream_limit` ──────────────────────────────────
 * The backend holds a bounded number of these connections, because each one
 * occupies a server worker thread for as long as the tab is open (see
 * core/events.py). Over that bound it answers with a single `stream_limit`
 * frame and closes, instead of accepting a connection it cannot serve. That
 * frame is the only way this module can learn WHY the stream went away — a
 * failed EventSource handshake reaches JavaScript as a bare `error` with no
 * status and no body — so it is what drives the 'capped' badge state and
 * the long backoff below.
 *
 * ── Why our own reconnect, when EventSource already retries ───────────────
 * EventSource's built-in retry is a fixed interval with no ceiling on
 * attempts and no awareness of why it failed. Against a backend that is down
 * for a deploy, every open tab hammers it at a constant rate forever. This
 * adds exponential backoff with jitter, stops entirely while the tab is
 * hidden or the browser is offline, and reconnects immediately on wake —
 * which is also what makes it survive a laptop sleeping, the exact failure
 * that took this app's Telegram sends down earlier.
 */

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

/**
 * The wait after the backend refused this stream because the tenant already
 * has as many open as the server will hold (its `stream_limit` frame).
 *
 * A floor, unlike the ordinary ladder, whose first rung is deliberately a
 * second: a backend that just restarted is back within seconds, so trying
 * again quickly costs one request and wins back live updates. A tenant at
 * its ceiling is not that — nothing changes until a person closes a tab, so
 * every fast retry is guaranteed waste, and it would come from every extra
 * tab at once.
 */
const STREAM_LIMIT_MIN_BACKOFF_MS = 30_000;
const STREAM_LIMIT_MAX_BACKOFF_MS = 60_000;

/** reason -> Set<handler>, plus '*' for subscribers that want everything. */
const subscribers = new Map();

let source = null;
let attempt = 0;
let reconnectTimer = null;
let currentKey = null;
let listenersBound = false;
/** Set by the last `stream_limit` frame, cleared by the next handshake. */
let refusedAtLimit = false;

function setState(state) {
  useUiStore.getState().setStreamState(state);
}

function emit(payload) {
  const reason = payload?.reason;
  const targets = [...(subscribers.get(reason) ?? []), ...(subscribers.get('*') ?? [])];
  targets.forEach((handler) => {
    try {
      handler(payload);
    } catch (error) {
      // One bad subscriber must not stop the others from seeing the event,
      // and must never take the connection down with it.
      console.error('[sse] subscriber threw', error);
    }
  });
}

function clearReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  clearReconnect();
  attempt += 1;
  let delay;
  if (refusedAtLimit) {
    // Somewhere in the last half of the window, not the whole of it: the
    // point of the jitter is still to keep the refused tabs from asking in
    // lockstep, but a random point in [0, cap] would let one of them try
    // again almost immediately, which is the reconnect storm this refusal
    // is supposed not to become.
    const span = STREAM_LIMIT_MAX_BACKOFF_MS - STREAM_LIMIT_MIN_BACKOFF_MS;
    delay = STREAM_LIMIT_MIN_BACKOFF_MS + Math.random() * span;
  } else {
    // Full jitter: a random point in [0, cap]. Without it every tab that
    // dropped on the same backend restart reconnects in lockstep and
    // re-creates the spike that knocked them off.
    delay = Math.random() * Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** (attempt - 1));
  }
  // 'capped' is kept rather than overwritten with 'reconnecting': the badge
  // would otherwise say "Atkuriamas ryšys…" forever about a connection that
  // is not coming back on its own, which is exactly the kind of plausible
  // wrong answer this app refuses to give.
  setState(refusedAtLimit ? 'capped' : 'reconnecting');
  reconnectTimer = setTimeout(connect, delay);
}

function connect() {
  clearReconnect();

  const { apiKey } = getAuth();
  if (!apiKey) {
    disconnect();
    setState('down');
    return;
  }

  // Pointless to hold a connection the OS has already severed; the online
  // listener reconnects the moment the network is back.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setState('offline');
    return;
  }

  disconnect();
  currentKey = apiKey;
  setState(attempt === 0 ? 'connecting' : 'reconnecting');

  // EventSource cannot set an Authorization header, so this one endpoint
  // takes the key as a query param — see resolve_api_key() in core/auth.py
  // for why that exception exists.
  source = new EventSource(`${API_BASE}/api/stream?api_key=${encodeURIComponent(apiKey)}`);

  source.addEventListener('connected', () => {
    attempt = 0; // a successful handshake resets the backoff ladder
    refusedAtLimit = false;
    useUiStore.getState().setStreamLimit(null);
    setState('live');
  });

  source.addEventListener('stream_limit', (event) => {
    // The backend will not hold another stream for this tenant, so it sends
    // this one frame and closes instead of parking the connection on a
    // worker thread every other request then has to do without. Read the
    // reason here, before the close arrives as a bare `error` that is
    // indistinguishable from a dead backend, and put the two numbers behind
    // the refusal on the badge — "6 of 6 open" is something a dispatcher can
    // act on, "Backend unreachable" is a wild goose chase.
    let payload = null;
    try {
      payload = JSON.parse(event.data);
    } catch {
      payload = null;
    }
    refusedAtLimit = true;
    useUiStore.getState().setStreamLimit(payload);
    // Drop and re-arm here rather than waiting for onerror: EventSource is
    // specified to stop delivering events once close() has run, so a server
    // that closes right after this frame may or may not surface an error
    // first, and a tab that reconnects only on the error path would sit
    // capped forever on the browsers that do not raise one.
    disconnect();
    scheduleReconnect();
  });

  // The server dropped events for this connection: its queue overflowed
  // because this tab fell too far behind (core/events.py's
  // MAX_QUEUED_EVENTS_PER_STREAM). Whatever this page is showing was built
  // from an incomplete stream, so everything is refetched rather than
  // trusted — a subscriber listening for one `reason` would otherwise never
  // hear the event it was waiting for.
  source.addEventListener('resync', (event) => {
    // Delivered to EVERY subscriber, not only those listening for
    // 'resync': a component waiting on 'order_updated' missed exactly the
    // event it was waiting for, and telling only the listeners of a reason
    // nobody subscribes to would be the same silence in a different shape.
    // Reuses the fan-out each subscriber already refetches on, rather than
    // reaching for a query client this module does not own — each bundle
    // creates its own (src/main.jsx, src/driver/main.jsx).
    let detail = {};
    try {
      detail = JSON.parse(event.data);
    } catch {
      detail = {};
    }
    const payload = { ...detail, reason: 'resync', at: Date.now() };
    useUiStore.getState().setLastEvent(payload);
    subscribers.forEach((handlers) => {
      handlers.forEach((handler) => {
        try {
          handler(payload);
        } catch (error) {
          console.error('[sse] subscriber threw on resync', error);
        }
      });
    });
  });

  source.addEventListener('changed', (event) => {
    let payload = null;
    try {
      payload = JSON.parse(event.data);
    } catch {
      return; // non-JSON keep-alive-ish frame; nothing to route
    }
    const enriched = { ...payload, at: Date.now() };
    useUiStore.getState().setLastEvent(enriched);
    emit(enriched);
  });

  // Confirms the connection is genuinely alive even on a quiet tenant with no
  // order activity — see app.py's stream(): this fires once on connect and at
  // least every 15s of idle thereafter (the same cadence the bare keep-alive
  // comment used before it carried a timestamp). `server_ts` is a one-way
  // freshness signal, not a round-trip latency measurement — SSE has no
  // client->server leg to time, so every consumer must call this
  // "lastHeartbeatAt", never "latency" or "ping".
  source.addEventListener('heartbeat', (event) => {
    let payload = null;
    try {
      payload = JSON.parse(event.data);
    } catch {
      return;
    }
    if (typeof payload?.server_ts !== 'number') return;
    useUiStore.getState().setLastHeartbeat({ serverTs: payload.server_ts, receivedAt: Date.now() });
  });

  source.onerror = () => {
    // EventSource reports both "server closed" and "network died" as a bare
    // error with no detail, so treat every one as a drop and back off.
    disconnect();
    scheduleReconnect();
  };
}

function disconnect() {
  if (source) {
    source.close();
    source = null;
  }
}

function bindLifecycleListeners() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;

  // Coming back from offline or from a hidden tab is the one moment worth
  // ignoring the backoff ladder for: the user is looking at the screen now.
  const resume = () => {
    if (document.visibilityState !== 'visible') return;
    if (!subscribers.size) return;
    attempt = 0;
    connect();
  };

  window.addEventListener('online', resume);
  window.addEventListener('offline', () => {
    disconnect();
    clearReconnect();
    setState('offline');
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // A stream that survived being backgrounded needs nothing; one that
      // died silently (laptop asleep, radio off) needs a fresh connection.
      if (!source) resume();
    }
  });

  // A 401 anywhere signs the session out; the stream must not keep trying
  // with a key the backend has already rejected.
  useAuthStore.subscribe((state) => {
    if (!state.apiKey) {
      disconnect();
      clearReconnect();
      setState('down');
      currentKey = null;
      refusedAtLimit = false;
      useUiStore.getState().setStreamLimit(null);
    } else if (state.apiKey !== currentKey) {
      attempt = 0;
      connect();
    }
  });
}

function subscribe(reasons, handler) {
  bindLifecycleListeners();

  const keys = reasons === '*' || reasons == null ? ['*'] : [].concat(reasons);
  keys.forEach((key) => {
    if (!subscribers.has(key)) subscribers.set(key, new Set());
    subscribers.get(key).add(handler);
  });

  // First subscriber opens the connection.
  if (!source && !reconnectTimer) connect();

  return () => {
    keys.forEach((key) => {
      const set = subscribers.get(key);
      if (!set) return;
      set.delete(handler);
      if (!set.size) subscribers.delete(key);
    });
    // Last one out closes it, so a signed-out or idle app holds nothing open.
    if (!subscribers.size) {
      disconnect();
      clearReconnect();
      attempt = 0;
      // This tab just gave its slot back. Whatever the server said about
      // being full described a moment that no longer includes us, so the
      // next subscriber starts from the fast ladder, not from the minute
      // this one was waiting out.
      refusedAtLimit = false;
      useUiStore.getState().setStreamLimit(null);
    }
  };
}

/**
 * Subscribe to server push.
 *
 *   useSSESubscription('dwell_exceeded', (payload) => …)
 *   useSSESubscription(['order_updated', 'status_changed'], onOrderChange)
 *   useSSESubscription('*', onAnything)
 *
 * The handler is kept in a ref, so passing an inline arrow function does not
 * tear down and re-open the subscription on every render — the mistake that
 * makes a "live" feature reconnect dozens of times a minute.
 */
export function useSSESubscription(reasons, handler, { enabled = true } = {}) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // `reasons` is usually a fresh array literal, so depending on it directly
  // would resubscribe on every render. The joined string is the same value
  // by identity across renders, and splitting it back gives the effect
  // everything it needs without capturing the array.
  const key = Array.isArray(reasons) ? reasons.join('|') : String(reasons ?? '*');

  useEffect(() => {
    if (!enabled) return undefined;
    const stable = (payload) => handlerRef.current?.(payload);
    return subscribe(key === '*' ? '*' : key.split('|'), stable);
  }, [key, enabled]);
}

/** Escape hatch for non-React callers and for tests. */
export const sseManager = { connect, disconnect, subscribe };
